import { uniqBy } from "es-toolkit";
import { curryRight } from "es-toolkit/compat";
import { useCallback, useMemo, useRef, useState, type PropsWithChildren } from "react";

import { pipe } from "../utilities";
import { SearchContext, type Filter, type FilterSet, type Status } from "./SearchContext";

type Props<T> = {
  source: T[];
  status: "error" | "pending" | "success";
  isFetching?: boolean;
  filterSets?: FilterSet<T>[];
  ordering?: Filter<T>;
};

type Processor<T> = (items: readonly T[], value: string, options: Record<string, unknown>) => T[];

const preserveOrder = <T,>(data: T[]) => data;

export const SearchContextProvider = <T,>({
  source,
  filterSets = [],
  ordering = preserveOrder,
  status,
  isFetching,
  children,
}: PropsWithChildren<Props<T>>) => {
  const [results, setResults] = useState<T[]>();

  const inputProcessor = useRef<(items: readonly T[]) => T[]>(undefined);

  const filteredData = useMemo(() => {
    let data = source;

    for (const { filters, operator } of filterSets) {
      const clone = structuredClone(data);

      data =
        operator === "and"
          ? pipe(clone, ...filters)
          : uniqBy(
              filters.flatMap((filter) => filter(clone)),
              (item) => JSON.stringify(item),
            );
    }

    return ordering(data);
  }, [filterSets, ordering, source]);

  const processInput = useCallback(
    (processor: Processor<T>, input: string, options: Record<string, unknown>) => {
      const trimmedInput = input.trim();

      const curried = curryRight(processor);
      inputProcessor.current = curried(trimmedInput, options);

      if (!filteredData) return;

      const newResults = trimmedInput ? inputProcessor.current?.(filteredData) : undefined;

      setResults(newResults ? ordering(newResults) : newResults);
    },
    [filteredData, ordering],
  );

  const reset = useCallback(() => setResults(undefined), []);

  const value = useMemo(
    () => ({
      source,
      filteredData,
      // when e.g. switching orgs we can refetch while we have data. The simplest option is
      // to just show the loading state again.
      status: (isFetching ? "processing" : status === "success" ? "ready" : "error") as Status,
      results,
      processInput,
      reset,
    }),
    [filteredData, processInput, reset, results, source, status, isFetching],
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};
